/**
 * Video Block Editor Component
 * Displays an empty-state placeholder or a video preview. Clicking either opens
 * the VideoSelectorModal. Metadata fields (title, descriptions) sit below.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type { JSONContent } from '@tiptap/core';
import {
  sanitizeRichTextContent,
  useTheme,
  type VideoBlock,
  type VideoTitleLevel,
  DEFAULT_CONTENT_LOCALE,
  useImageSourceWithFallback,
} from '@course-authoring/shared-types';
import {
  VideoSelectorModal,
  formatDuration,
  type VideoSelectionResult,
  type CurrentVideoData,
} from './VideoSelectorModal';
import { checkTranscriptAvailability, getVideo } from '../api/centerstageApi';
import { useLanguageContext } from '../hooks/useLanguageContext';
import type { ImageAreaState } from './blockEditorStyles';
import { TitleLevelSelector, getTitleInputStyle } from './TitleLevelSelector';
import { InteractionRichTextField } from './InteractionRichTextField';
import { createTokenRichTextToolbarStyles } from './interactionEditorUtils';

const SHORT_DESCRIPTION_MAX_LENGTH = 175;

const EMPTY_RICH_TEXT_DOC: JSONContent = {
  type: 'doc',
  content: [{ type: 'paragraph' }],
};

function normalizeLongDescription(
  longDescription: string | JSONContent | undefined
): JSONContent {
  if (!longDescription) {
    return EMPTY_RICH_TEXT_DOC;
  }
  if (typeof longDescription === 'string') {
    if (!longDescription.trim()) {
      return EMPTY_RICH_TEXT_DOC;
    }
    const paragraphs = longDescription
      .split(/\n+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    return sanitizeRichTextContent({
      type: 'doc',
      content: (paragraphs.length > 0 ? paragraphs : ['']).map((p) => ({
        type: 'paragraph',
        ...(p.length > 0 ? { content: [{ type: 'text', text: p }] } : {}),
      })),
    });
  }
  return sanitizeRichTextContent(longDescription);
}

interface VideoBlockEditorProps {
  block: VideoBlock;
  onChange: (updates: Partial<VideoBlock>) => void;
  autoFocus?: boolean;
  translationMode?: boolean;
}

export function VideoBlockEditor({
  block,
  onChange,
  autoFocus = false,
  translationMode = false,
}: VideoBlockEditorProps) {
  const { tokens } = useTheme();
  const titleFieldId = useId();
  const shortDescriptionFieldId = useId();
  const containerRef = useRef<HTMLDivElement>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [videoAreaState, setVideoAreaState] = useState<ImageAreaState>({
    isFocused: false,
    isHovered: false,
  });
  const [isLongDescFocused, setIsLongDescFocused] = useState(false);
  const [duration, setDuration] = useState<number | undefined>(undefined);
  const { selectedLocale } = useLanguageContext();
  const transcriptLocale = (selectedLocale || DEFAULT_CONTENT_LOCALE).toLowerCase();
  // undefined = checking (or check failed); only a definite answer is shown.
  const [hasTranscript, setHasTranscript] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    setHasTranscript(undefined);
    const id = block.centerstageVideoId;
    if (!id) return;
    const controller = new AbortController();
    checkTranscriptAvailability(id, transcriptLocale, controller.signal)
      .then((exists) => {
        if (!controller.signal.aborted) setHasTranscript(exists);
      })
      .catch(() => { /* leave status hidden */ });
    return () => controller.abort();
  }, [block.centerstageVideoId, transcriptLocale]);

  useEffect(() => {
    setDuration(undefined);
    const id = block.centerstageVideoId;
    if (!id) return;
    let cancelled = false;
    getVideo(id).then((details) => {
      if (!cancelled && details?.duration !== undefined) {
        setDuration(details.duration);
      }
    }).catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, [block.centerstageVideoId]);

  const titleLevel: VideoTitleLevel = block.titleLevel ?? 'h2';
  const hasVideo = Boolean(block.url);
  const currentShortDescriptionLength = (block.shortDescription || '').length;
  const {
    resolvedSrc: resolvedPosterUrl,
    handleError: handlePosterError,
  } = useImageSourceWithFallback(block.posterUrl);

  const longDescContent = useMemo(
    () => normalizeLongDescription(block.longDescription),
    [block.longDescription]
  );

  const toolbarStyles = useMemo(
    () => createTokenRichTextToolbarStyles(tokens),
    [tokens]
  );

  const styles = useMemo(
    () => createStyles(tokens, videoAreaState, hasVideo),
    [tokens, videoAreaState, hasVideo]
  );

  const handleSelect = useCallback(
    (data: VideoSelectionResult) => {
      const updates: Partial<VideoBlock> = {
        url: data.url,
        posterUrl: data.posterUrl || undefined,
        tracks: data.tracks,
        centerstageVideoId: data.centerstageVideoId || undefined,
      };

      if (data.title) {
        updates.title = data.title;
      }

      onChange(updates);
      setIsModalOpen(false);
    },
    [onChange]
  );

  const handleRemove = useCallback(() => {
    onChange({
      url: '',
      posterUrl: undefined,
      tracks: [{ kind: 'captions', src: '', label: 'English', language: 'en' }],
      centerstageVideoId: undefined,
    });
  }, [onChange]);

  const openModal = useCallback(() => setIsModalOpen(true), []);

  const currentVideo: CurrentVideoData | undefined = hasVideo
    ? {
        url: block.url,
        posterUrl: block.posterUrl,
        centerstageVideoId: block.centerstageVideoId,
        title: block.title,
      }
    : undefined;

  return (
    <div ref={containerRef} style={styles.container}>
      {/* Video area */}
      <div
        role={translationMode ? undefined : 'button'}
        tabIndex={translationMode ? undefined : 0}
        aria-label={translationMode ? undefined : hasVideo ? 'Change video' : 'Add video'}
        style={{ ...styles.videoAreaWrapper, ...(translationMode ? { cursor: 'default' } : {}) }}
        onClick={translationMode ? undefined : openModal}
        onKeyDown={translationMode ? undefined : (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openModal();
          }
        }}
        onFocus={translationMode ? undefined : () => setVideoAreaState((prev) => ({ ...prev, isFocused: true }))}
        onBlur={translationMode ? undefined : () => setVideoAreaState((prev) => ({ ...prev, isFocused: false }))}
        onMouseEnter={translationMode ? undefined : () => setVideoAreaState((prev) => ({ ...prev, isHovered: true }))}
        onMouseLeave={translationMode ? undefined : () => setVideoAreaState((prev) => ({ ...prev, isHovered: false }))}
      >
        <div style={styles.videoAreaInner}>
          {hasVideo ? (
            <>
              {resolvedPosterUrl ? (
                <img
                  src={resolvedPosterUrl}
                  alt={block.title || 'Video thumbnail'}
                  style={styles.thumbnailImage}
                  crossOrigin="anonymous"
                  onError={handlePosterError}
                />
              ) : (
                <div style={styles.thumbnailFallback}>
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <polygon points="5,3 19,12 5,21" />
                  </svg>
                </div>
              )}
              {!translationMode ? (
                <div style={styles.videoOverlay}>
                  <span style={styles.videoOverlayText}>Click to change video</span>
                </div>
              ) : null}
            </>
          ) : (
            <div style={styles.placeholder}>
              <svg
                style={styles.placeholderIcon}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <polygon points="10,8 16,12 10,16" />
              </svg>
              <span style={styles.placeholderText}>{translationMode ? 'No source video' : 'Click to add video'}</span>
            </div>
          )}
        </div>
      </div>

      {hasVideo && block.centerstageVideoId && (
        <div style={styles.videoCaption}>
          CenterStage #{block.centerstageVideoId}
          {duration !== undefined ? ` - ${formatDuration(duration)}` : ''}
          {hasTranscript !== undefined && (
            <span data-testid="video-transcript-status">
              {' | '}
              {hasTranscript ? 'Transcript available' : 'No transcript'}
            </span>
          )}
        </div>
      )}

      {/* Metadata fields */}
      <div style={styles.metadataSection}>
        {!translationMode ? <TitleLevelSelector
          currentLevel={titleLevel}
          onSelectLevel={(level) => onChange({ titleLevel: level as VideoTitleLevel })}
          tokens={tokens}
        /> : null}

        <div style={styles.fieldGroup}>
          <input
            id={titleFieldId}
            type="text"
            value={block.title || ''}
            onChange={(e) => onChange({ title: e.target.value })}
            placeholder="Enter video title…"
            style={getTitleInputStyle(titleLevel, tokens)}
            aria-label="Video title"
          />
        </div>

        <div style={styles.fieldGroup}>
          <div style={styles.fieldHeader}>
            <label htmlFor={shortDescriptionFieldId} style={styles.fieldLabel}>
              Short Description
            </label>
            <span style={styles.characterCount}>
              {currentShortDescriptionLength}/{SHORT_DESCRIPTION_MAX_LENGTH}
            </span>
          </div>
          <textarea
            id={shortDescriptionFieldId}
            value={block.shortDescription || ''}
            onChange={(e) => onChange({ shortDescription: e.target.value })}
            placeholder="Enter short description…"
            style={styles.metadataTextarea}
            rows={3}
            maxLength={SHORT_DESCRIPTION_MAX_LENGTH}
            aria-label="Video short description"
          />
          <p style={styles.fieldHint}>
            Appears over the video poster in the learner view. Maximum 175 characters.
          </p>
        </div>

        <div style={styles.fieldGroup}>
          <span style={styles.fieldLabel}>Long Description</span>
          <InteractionRichTextField
            content={longDescContent}
            placeholder="Enter long description…"
            isFocused={isLongDescFocused}
            onFocus={() => setIsLongDescFocused(true)}
            onBlur={() => setIsLongDescFocused(false)}
            onContentChange={(content) => onChange({ longDescription: content })}
            autoFocus={false}
            containerRef={containerRef}
            toolbarPreset="full"
            toolbarStyles={toolbarStyles}
          />
          <p style={styles.fieldHint}>
            Displays below the video in the learner view when provided.
          </p>
        </div>
      </div>

      {!translationMode ? <VideoSelectorModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSelect={handleSelect}
        onRemove={handleRemove}
        currentVideo={currentVideo}
      /> : null}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

function createStyles(
  tokens: ReturnType<typeof useTheme>['tokens'],
  videoAreaState: ImageAreaState,
  hasVideo: boolean
): Record<string, CSSProperties> {
  const showInteraction = videoAreaState.isHovered || videoAreaState.isFocused;

  return {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    },

    videoAreaWrapper: {
      position: 'relative',
      width: '70%',
      margin: '0 auto',
      borderRadius: tokens.radius.md,
      overflow: 'hidden',
      cursor: 'pointer',
      outline: videoAreaState.isFocused
        ? `2px solid ${tokens.ui.selectionBorder}`
        : 'none',
      outlineOffset: '2px',
      border: hasVideo ? 'none' : `1px solid ${tokens.borders.default}`,
      backgroundColor: tokens.backgrounds.page,
    },
    videoAreaInner: {
      position: 'relative',
      width: '100%',
      paddingBottom: '56.25%',
      overflow: 'hidden',
    },

    thumbnailImage: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    thumbnailFallback: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: tokens.backgrounds.surfaceAlt,
      color: tokens.text.muted,
    },
    videoOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      opacity: showInteraction ? 1 : 0,
      transition: 'opacity 150ms',
    },
    videoOverlayText: {
      color: tokens.text.onInverse,
      fontSize: tokens.typography.body.size,
      fontFamily: tokens.typography.fontFamily,
      fontWeight: 600,
    },

    videoCaption: {
      width: '70%',
      margin: '0 auto',
      textAlign: 'left',
      fontSize: '12px',
      color: tokens.text.muted,
      fontFamily: tokens.typography.fontFamily,
    },

    placeholder: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: tokens.spacing.sm,
      color: tokens.text.muted,
      backgroundColor: showInteraction
        ? tokens.backgrounds.surfaceAlt
        : tokens.backgrounds.page,
      transition: 'background-color 150ms',
    },
    placeholderIcon: {
      width: '42px',
      height: '42px',
      color: tokens.text.muted,
    },
    placeholderText: {
      fontSize: tokens.typography.body.size,
      fontFamily: tokens.typography.fontFamily,
      color: tokens.text.muted,
    },

    metadataSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      padding: '20px',
      backgroundColor: tokens.backgrounds.page,
      borderRadius: '8px',
      border: `1px solid ${tokens.borders.default}`,
    },
    fieldGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    fieldHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      gap: '12px',
    },
    fieldLabel: {
      fontSize: '14px',
      fontWeight: 600,
      color: tokens.text.primary,
      fontFamily: tokens.typography.fontFamily,
    },
    characterCount: {
      fontSize: '12px',
      color: tokens.text.muted,
      fontFamily: tokens.typography.fontFamily,
    },
    fieldHint: {
      margin: 0,
      fontSize: '12px',
      color: tokens.text.muted,
      lineHeight: '1.4',
      fontFamily: tokens.typography.fontFamily,
    },
    metadataInput: {
      width: '100%',
      padding: '10px 12px',
      fontSize: '14px',
      color: tokens.text.primary,
      backgroundColor: tokens.backgrounds.page,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: '6px',
      boxSizing: 'border-box',
      fontFamily: tokens.typography.fontFamily,
    },
    metadataTextarea: {
      width: '100%',
      minHeight: '88px',
      padding: '10px 12px',
      fontSize: '14px',
      color: tokens.text.primary,
      backgroundColor: tokens.backgrounds.page,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: '6px',
      resize: 'vertical',
      boxSizing: 'border-box',
      fontFamily: tokens.typography.fontFamily,
      lineHeight: '1.5',
    },
  };
}
